import type { ChallengeEditorSectionHandle } from '@/components/challenges/editor/types';
import type { OwnerScopeProps } from '@/components/shared/ownerScope';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Trash2, Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface OwnerArtifactSummary {
  id: string;
  name: string;
  artifactType: string;
  artifactCategory: string;
}

interface RewardLink {
  artifactId: string;
  quantityAwarded: number;
}

interface Props extends Omit<OwnerScopeProps, 'structureEditable'> {
  /** Event challenge id or template challenge-link id, per the owner scope. */
  challengeId: string;
  /**
   * `standalone` (default) renders the section's own Save button + toasts.
   * `controlled` hides them and exposes `{ isDirty, save }` via
   * `onRegisterHandle` so a parent editor's save pipeline drives persistence.
   */
  mode?: 'standalone' | 'controlled';
  /** Required in controlled mode: receives the save handle (null on unmount). */
  onRegisterHandle?: (handle: ChallengeEditorSectionHandle | null) => void;
}

/** Order-sensitive structural equality via JSON (rows aren't reorderable). */
function sameLinks(a: readonly RewardLink[], b: readonly RewardLink[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Awards editor for a single challenge in an owner scope (event or template):
 * which artifacts (gadgets / power-ups) the team receives on completion. Loads
 * the owner's artifacts + the challenge's existing reward links, and PUTs the
 * full replacement list — via its own Save button (standalone) or the parent
 * editor's pipeline (controlled).
 */
export function ChallengeArtifactsSection({
  ownerId,
  apiBase,
  keyPrefix,
  challengeId,
  mode = 'standalone',
  onRegisterHandle,
}: Props) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const isStandalone = mode === 'standalone';
  const ownerNoun = keyPrefix === 'Event' ? 'event' : 'template';

  const { data: catalogResp, isLoading: catalogLoading } = useQuery<{
    success: boolean;
    data: OwnerArtifactSummary[];
  }>({
    queryKey: [`${keyPrefix}Artifacts`, ownerId],
    queryFn: () => apiClient.get(`${apiBase}/artifacts`),
    enabled: !!ownerId,
  });
  const catalog = catalogResp?.data ?? [];

  const { data: rewardsResp } = useQuery<{
    success: boolean;
    data: Array<{ id: string; quantityAwarded: number; artifact: OwnerArtifactSummary }>;
  }>({
    queryKey: ['ChallengeArtifactRewards', ownerId, challengeId],
    queryFn: () =>
      apiClient.get(`${apiBase}/challenges/${challengeId}/artifact-rewards`),
    enabled: !!ownerId && !!challengeId,
  });

  const [rewards, setRewards] = useState<RewardLink[]>([]);

  // Baseline + latest-state refs so the controlled-mode handle can read
  // current dirty state without re-registering on every change.
  const baselineRef = useRef<RewardLink[]>([]);
  const rewardsRef = useRef<RewardLink[]>(rewards);
  rewardsRef.current = rewards;

  useEffect(() => {
    if (rewardsResp?.data) {
      const next = rewardsResp.data.map((r) => ({
        artifactId: r.artifact.id,
        quantityAwarded: r.quantityAwarded,
      }));
      setRewards(next);
      baselineRef.current = next;
    }
  }, [rewardsResp]);

  const saveRewardsMutation = useMutation({
    mutationFn: async (payload: RewardLink[]) =>
      apiClient.put(`${apiBase}/challenges/${challengeId}/artifact-rewards`, {
        rewards: payload,
      }),
    onSuccess: (_resp, payload) => {
      baselineRef.current = payload;
      queryClient.invalidateQueries({
        queryKey: ['ChallengeArtifactRewards', ownerId, challengeId],
      });
      if (isStandalone) toast.success('Artifact rewards saved');
    },
    onError: (err: Error) => {
      if (isStandalone) toast.error(err?.message || 'Failed to save artifact rewards');
    },
  });

  const saveRewardsRef = useRef(saveRewardsMutation);
  saveRewardsRef.current = saveRewardsMutation;

  useEffect(() => {
    if (!onRegisterHandle) return undefined;
    onRegisterHandle({
      label: 'artifact rewards',
      isDirty: () => !sameLinks(rewardsRef.current, baselineRef.current),
      save: async () => {
        await saveRewardsRef.current.mutateAsync(rewardsRef.current);
      },
    });
    return () => onRegisterHandle(null);
  }, [onRegisterHandle]);

  if (catalogLoading) {
    return <Skeleton className="h-48 rounded-xl" />;
  }

  if (catalog.length === 0) {
    return (
      <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
        <CardHeader className="px-6 py-4 !pb-4 border-b">
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Artifact Rewards
          </CardTitle>
          <CardDescription>
            Add artifacts to this {ownerNoun} before granting them from challenges.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground">
            No {ownerNoun} artifacts found. Visit the {ownerNoun}&apos;s &quot;Artifacts&quot; tab to add some.
          </p>
        </CardContent>
      </Card>
    );
  }

  const artifactNameById = new Map(catalog.map((c) => [c.id, c.name]));
  const availableForReward = catalog.filter(
    (c) => !rewards.some((r) => r.artifactId === c.id),
  );

  return (
    <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
      <CardHeader className="px-6 py-4 !pb-4 border-b">
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Artifact Rewards
        </CardTitle>
        <CardDescription>
          Grant artifacts (gadgets / power-ups) to the team when this challenge is completed.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">Awards on completion</h4>
          {isStandalone && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => saveRewardsMutation.mutate(rewards)}
              loading={saveRewardsMutation.isPending}
            >
              <Save className="h-3.5 w-3.5" />
              Save Awards
            </Button>
          )}
        </div>

        {rewards.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Completing this challenge grants no artifacts.
          </p>
        )}

        <div className="space-y-2">
          {rewards.map((row, idx) => (
            <div key={`${row.artifactId}-${idx}`} className="flex items-center gap-2">
              <div className="flex-1 text-sm font-medium">
                {artifactNameById.get(row.artifactId) ?? row.artifactId}
              </div>
              <Input
                type="number"
                min={1}
                value={row.quantityAwarded}
                onChange={(e) =>
                  setRewards((prev) =>
                    prev.map((r, i) =>
                      i === idx ? { ...r, quantityAwarded: parseInt(e.target.value) || 1 } : r,
                    ),
                  )
                }
                className="w-24"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRewards((prev) => prev.filter((_, i) => i !== idx))}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>

        {availableForReward.length > 0 && (
          <Select
            onValueChange={(value) =>
              setRewards((prev) => [...prev, { artifactId: value, quantityAwarded: 1 }])
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Add an artifact to award..." />
            </SelectTrigger>
            <SelectContent>
              {availableForReward.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </CardContent>
    </Card>
  );
}
